import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';

const getTypeIcon = (type, title) => {
  if (type === 'rank_up' || title?.toLowerCase()?.includes('rank-up') || title?.toLowerCase()?.includes('rankup')) return '🎉';
  if (type === 'tier_lost' || title?.toLowerCase()?.includes('tier perdu')) return '⚠️';
  if (type === 'inactive' || title?.toLowerCase()?.includes('inactiv')) return '😴';
  if (type === 'contract' || title?.toLowerCase()?.includes('contrat')) return '📋';
  if (type === 'injection' || title?.toLowerCase()?.includes('injection')) return '💉';
  if (type === 'never_live' || title?.toLowerCase()?.includes('jamais')) return '🎥';
  if (type === 'activeness' || title?.toLowerCase()?.includes('activeness')) return '📈';
  if (type === 'manual') return '✉️';
  return '🔔';
};

const getRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "à l'instant";
  if (diffMins < 60) return `il y a ${diffMins}min`;
  if (diffHours < 24) return `il y a ${diffHours}h`;
  if (diffDays === 1) return 'hier';
  if (diffDays < 7) return `il y a ${diffDays}j`;
  return date?.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const NotificationCard = ({ notification, onMarkRead }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const isRead = !!notification?.read_at;
  const icon = getTypeIcon(notification?.type, notification?.title);
  const relTime = getRelativeTime(notification?.created_at);
  const bodyHasMultipleLines = (notification?.body || '').includes('\n') || (notification?.body || '').length > 100;

  const handleMarkRead = async (e) => {
    e?.stopPropagation();
    if (isRead) return;
    const { error } = await supabase
      ?.from('notifications')
      ?.update({ read_at: new Date()?.toISOString() })
      ?.eq('id', notification?.id);
    if (!error) onMarkRead?.(notification?.id);
  };

  const handleClick = () => {
    // Navigate to link if present, mark as read
    if (notification?.link) {
      if (!isRead) handleMarkRead();
      navigate(notification.link);
      return;
    }
    if (bodyHasMultipleLines) setExpanded(e => !e);
  };

  const targetLabel = notification?.target_type === 'admin' ? 'Admin'
    : notification?.users?.name || notification?.users?.email || 'Manager';

  // Detect links in body (e.g. /admin/violations)
  const renderBody = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      if (line.startsWith('/admin/') || line.startsWith('/manager/')) {
        return (
          <a key={i} href={line.trim()} style={{ color: '#f97316', textDecoration: 'underline' }}>
            {line.trim()}
          </a>
        );
      }
      // Bold section headers (all caps lines)
      if (line === line.toUpperCase() && line.length > 3 && /^[A-Z\s\-()0-9]+$/.test(line.trim())) {
        return <span key={i} className="block mt-2 mb-1 font-bold" style={{ color: 'rgba(255,255,255,0.8)' }}>{line}</span>;
      }
      return <span key={i} className="block">{line}</span>;
    });
  };

  return (
    <div
      onClick={handleClick}
      className="rounded-xl border p-4 flex gap-4 transition-all duration-150"
      style={{
        backgroundColor: isRead ? '#0d0d0d' : '#ffffff08',
        borderColor: isRead ? '#1f1f1f' : '#E5202A',
        borderLeftWidth: isRead ? '1px' : '3px',
        cursor: (notification?.link || bodyHasMultipleLines) ? 'pointer' : 'default',
      }}
    >
      <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-xl"
        style={{ backgroundColor: '#1a1a1a' }}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold text-sm truncate" style={{ fontFamily: 'Sora, sans-serif' }}>
              {notification?.title}
              {bodyHasMultipleLines && (
                <span className="ml-2 text-white/30 text-xs font-normal">{expanded ? '▲' : '▼'}</span>
              )}
            </p>
            {expanded ? (
              <div className="text-white/60 text-xs mt-1.5 whitespace-pre-line" style={{ fontFamily: 'Inter, sans-serif', lineHeight: '1.6' }}>
                {renderBody(notification?.body)}
              </div>
            ) : (
              <p className="text-white/60 text-xs mt-0.5 line-clamp-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                {notification?.body}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-white/30 text-xs" style={{ fontFamily: 'Inter, sans-serif' }}>{relTime}</span>
            {!isRead && (
              <button
                onClick={handleMarkRead}
                className="px-2.5 py-1 rounded-lg text-xs font-medium transition-all"
                style={{
                  backgroundColor: 'rgba(229,32,42,0.1)',
                  color: '#E5202A',
                  fontFamily: 'Inter, sans-serif',
                  border: '1px solid rgba(229,32,42,0.2)',
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(229,32,42,0.2)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(229,32,42,0.1)'}
              >
                {t('admin.notificationsRules.markRead', 'Mark as read')}
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 mt-2">
          <span className="text-xs px-2 py-0.5 rounded-full"
            style={{
              backgroundColor: notification?.type === 'manual' ? 'rgba(139,92,246,0.15)' : 'rgba(255,107,0,0.15)',
              color: notification?.type === 'manual' ? '#8B5CF6' : '#FF6B00',
              fontFamily: 'Inter, sans-serif',
            }}>
            {notification?.type === 'manual' ? t('admin.notificationsPage.manual', 'Manual') : t('admin.notificationsPage.auto', 'Auto')}
          </span>
          <span className="text-white/40 text-xs" style={{ fontFamily: 'Inter, sans-serif' }}>
            → {targetLabel}
          </span>
          {!isRead && (
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--color-primary)' }} />
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationCard;
